import { DestroyRef, Injectable, effect, inject, untracked } from '@angular/core';
import { ACCESS_TOKEN_REFRESH_MARGIN_SECONDS, SessionStore } from '../state/session.store';
import { TokenRefreshCoordinator } from './token-refresh-coordinator.service';

const MIN_REFRESH_DELAY_MS = 1_000;
@Injectable({ providedIn: 'root' })
export class TokenRefreshScheduler {
  private readonly session = inject(SessionStore);
  private readonly coordinator = inject(TokenRefreshCoordinator);

  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const payload = this.session.tokenPayload();
      untracked(() => this.schedule(payload?.exp ?? null, payload?.refresh_token_expiration ?? null));
    });

    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  private schedule(expSeconds: number | null, refreshExpSeconds: number | null): void {
    this.clearTimer();

    if (expSeconds === null || refreshExpSeconds === null) {
      return;
    }

    const nowSeconds = Date.now() / 1000;
    if (refreshExpSeconds <= nowSeconds) {
      return;
    }

    const refreshAtSeconds = expSeconds - ACCESS_TOKEN_REFRESH_MARGIN_SECONDS;
    const delayMs = Math.max(MIN_REFRESH_DELAY_MS, (refreshAtSeconds - nowSeconds) * 1000);

    this.timerId = setTimeout(() => {
      this.timerId = null;
      if (this.session.isRefreshTokenExpired()) {
        return;
      }
      this.coordinator.refresh().subscribe({ error: () => undefined });
    }, delayMs);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
